import React, { useState } from 'react';
import { Button, Box, Text, Alert, AlertIcon } from '@chakra-ui/react';
import { useFeatureFlagsFromContext } from '../../launchdarkly';

/**
 * SSO Login Button Component
 * Handles the initiation of Okta SSO authentication flow via BFF
 *
 * This component redirects to the BFF (Backend for Frontend) which handles:
 * - PKCE parameter generation
 * - OAuth flow with Okta
 * - Token exchange and secure storage
 * - Session creation with HttpOnly cookies
 *
 * This approach is more secure than browser-based OAuth because:
 * - Tokens are never exposed to JavaScript (XSS protection)
 * - PKCE code_verifier stays server-side
 * - Refresh tokens handled securely by BFF
 */
const SSOLoginButton = ({
  variant = 'solid',
  colorScheme = 'blue',
  size = 'lg',
  width = 'full',
  ...props
}) => {
  const flags = useFeatureFlagsFromContext();
  const oktaSsoAuthEnabled = flags?.oktaSsoAuth || false;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSSOLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get BFF URL from environment variable
      const bffUrl = process.env.REACT_APP_PROFIT_ASSIST_BFF_URL;

      if (!bffUrl) {
        throw new Error(
          'SSO is not properly configured. Please contact support.'
        );
      }

      // Get current page URL to return to after authentication
      const returnUrl = encodeURIComponent(
        window.location.pathname + window.location.search
      );

      // Redirect to BFF bootstrap endpoint to start OAuth flow
      // The BFF will:
      // 1. Generate PKCE parameters
      // 2. Redirect to Okta for authentication
      // 3. Handle the callback from Okta
      // 4. Exchange authorization code for tokens
      // 5. Create a session and set PA_AUTH cookie
      // 6. Redirect back to the frontend
      const bootstrapUrl = `${bffUrl}/api/v1/auth/bootstrap?redirect=${returnUrl}`;

      console.log('Initiating SSO login via BFF:', bootstrapUrl);

      // Redirect to BFF which will handle the OAuth flow
      window.location.href = bootstrapUrl;
    } catch (err) {
      console.error('Error initiating SSO login:', err);
      setError(
        err.message || 'Failed to initiate SSO login. Please try again.'
      );
      setIsLoading(false);
    }
  };

  // Don't render if SSO is not enabled
  if (!oktaSsoAuthEnabled) {
    return null;
  }

  return (
    <Box width="100%">
      {error && (
        <Alert status="error" marginBottom={4} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">{error}</Text>
        </Alert>
      )}

      <Button
        variant={variant}
        colorScheme={colorScheme}
        size={size}
        width={width}
        onClick={handleSSOLogin}
        isLoading={isLoading}
        loadingText="Redirecting to SSO..."
        disabled={isLoading}
        {...props}
      >
        Sign in with SpotOn
      </Button>
    </Box>
  );
};

export default SSOLoginButton;
